import {
  Bitmap,
  BitmapData,
  Color,
  MovieClip,
  TextField,
  TextFormat,
} from "../runtime";
import {
  G_portraitHolder,
  G_textBox,
  portrait_A1,
  portrait_A2,
  portrait_C1,
  portrait_C2,
  portrait_G1,
  portrait_G2,
  portrait_X1,
} from "./assets";
import { Effect } from "./Effect";
import { EffectId } from "./ids";
import {
  FONT_BODY,
  FONT_DISPLAY,
  TEXT_COLOURS,
  createTextField,
} from "./textStyle";

type PortraitCtor = new () => BitmapData;

/** Caption above the box, by speaker id. Anything past the table stays unnamed. */
const SPEAKER_NAMES: readonly string[] = [
  "ASTERUS",
  "LUMI",
  "DR CRENSON",
  "XENOS CORE",
  "?????",
];
const UNKNOWN_SPEAKER = "?????";

/** Portrait art per speaker, indexed by pose. Speakers 3 and up all use the Xenos plate. */
const PORTRAITS: readonly (readonly PortraitCtor[])[] = [
  [portrait_A1, portrait_A2],
  [portrait_G1, portrait_G2],
  [portrait_C1, portrait_C2],
];

/** The dialogue box: scales open, types its line out, then scales shut. */
export class TextBox extends MovieClip {
  private readonly format = new TextFormat();
  private readonly formatSpeaker = new TextFormat();
  private readonly speakerText: string;
  /** remaining characters to type, one per `addText` call */
  private readonly txtArray: string[];
  private readonly portraitDistort: Effect | null = null;
  private txt!: TextField;
  private txtSpeaker!: TextField;
  private txtCurrent = "";
  private txtOn = false;
  private alphaCurrent = 0;
  private scaleCurrent = 0;

  constructor(displayText = "", sp = 0, p = 0) {
    super();
    this.x = 360;
    this.y = 385;
    this.format.align = "left";
    this.format.font = FONT_BODY;
    this.format.color = TEXT_COLOURS.white;
    this.format.size = 20;
    this.format.leading = -4;
    this.formatSpeaker.align = "right";
    this.formatSpeaker.bold = true;
    this.formatSpeaker.font = FONT_DISPLAY;
    this.formatSpeaker.color = TEXT_COLOURS.aqua;
    this.formatSpeaker.size = 28;
    this.speakerText = SPEAKER_NAMES[sp] ?? UNKNOWN_SPEAKER;

    const tBoxHolder = new MovieClip();
    this.addChild(tBoxHolder);
    const tBoxSpr = new Bitmap(new G_textBox());
    tBoxSpr.x = tBoxSpr.width * -0.5;
    tBoxSpr.y = tBoxSpr.height * -0.5;
    tBoxHolder.addChild(tBoxSpr);
    this.scaleY = 0;
    this.alphaCurrent = 0;
    this.txtArray = displayText.split("");

    const portraitHolder = new MovieClip();
    portraitHolder.x = -300;
    portraitHolder.y = -64;
    this.addChild(portraitHolder);
    const portrait = sp >= PORTRAITS.length ? portrait_X1 : PORTRAITS[sp][p];
    portraitHolder.addChild(new Bitmap(new portrait()));
    portraitHolder.addChild(new Bitmap(new G_portraitHolder()));
    if (sp > 0) {
      this.portraitDistort = new Effect(EffectId.PortraitDistortion, 64, 64);
      portraitHolder.addChild(this.portraitDistort);
    }
  }

  /** Scales the box open, then types its line out two characters per tick. */
  update(): void {
    if (this.alphaCurrent < 100) {
      this.alphaCurrent += 20;
      this.alpha = this.alphaCurrent * 0.01;
    }
    if (this.scaleCurrent < 100) {
      this.scaleCurrent += 10;
      this.scaleY = this.scaleCurrent * 0.01;
      this.updateGlow();
      if (this.scaleCurrent === 100) {
        this.generateText();
      }
    }
    if (this.portraitDistort != null) {
      this.portraitDistort.updateAlpha();
      this.portraitDistort.updatePortraitDistort();
    }
    if (this.txtOn) {
      // two characters per tick
      this.addText();
      this.addText();
    }
  }

  private addText(): void {
    const next = this.txtArray.shift();
    if (next == null) {
      return;
    }
    this.txtCurrent += next === "$" ? "\n" : next;
    this.txt.text = this.txtCurrent;
  }

  private updateGlow(): void {
    const c = new Color();
    c.setTint(16777215, this.scaleCurrent * -0.01 + 1);
    this.transform.colorTransform = c;
  }

  private generateText(): void {
    this.txt = createTextField(this, this.format, {
      width: 460,
      height: 115,
      x: -160,
      y: -29,
      text: "",
    });
    this.txtSpeaker = createTextField(this, this.formatSpeaker, {
      width: 200,
      height: 30,
      x: 67,
      y: -74,
      text: this.speakerText,
      wordWrap: false,
    });
    this.txtOn = true;
  }
}
